import { useState } from 'react'
import { Controller, SubmitHandler, useForm } from 'react-hook-form'

import {
  Autocomplete,
  Button,
  Dialog,
  Icon,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  StateParams,
  Textarea,
  Textfield
} from '@clientbase/clientbase-library'
import { yupResolver } from '@hookform/resolvers/yup'
import { Box, Grid, Stack, Tooltip, Typography } from '@mui/material'
import useModuleAccess from 'hooks/useModuleAccess'
import { AutoCompleteBillingsParams } from 'models/Billings/AutoComplete'
import moment from 'moment'

import CanLogical from 'components/CanLogical'
import { DatePicker } from 'components/DatePicker'
import IconContainer from 'components/IconContainer/IconContainer'
import { EmitNfseOnReceiptSwitch } from 'components/Modal/ModalEmitNfseForCharge'
import SourceAutocomplete from 'components/Modal/ModalNewExpense/components/autocompletes/SourceAutocomplete'
import UploadComponent from 'components/UploadComponent'

import {
  currency,
  formatCentsToSendToBackEnd,
  formatMoney,
  onlyNumber
} from 'utils'
import { formatDate } from 'utils/FormatDate'
import { SETTLEMENT_ACCOUNT_HINT } from 'utils/settlement-account'
import { toastFirstError } from 'utils/yupResolverWithToast'

import { theme } from 'styles/theme'

import { schemaFormMarkAsPaid } from '../schemaMarkAsPaid'
import {
  shouldWarnZeroAmountNfse,
  ZeroAmountNfseContext
} from './markAsPaidNfse.utils'

export interface MarkAsPaidModalForm {
  amountPaid: string
  datePaid: Date
  directType: DirectTypeParams
  comment: string
  /** Conta onde o dinheiro entrou. Opcional, e só existe para quem tem contas a receber. */
  source?: { label: string; value: string }
  /** Abrir a emissão de nota fiscal assim que o recebimento for registrado. */
  emitNfse?: boolean
}

export type SubmitMarkAsPaidOptions = {
  /**
   * O agendamento da nota foi cancelado logo antes deste pagamento — muda as
   * mensagens de sucesso e de erro.
   */
  nfseScheduledCancelled?: boolean
}

export type SubmitMarkAsPaidHandler = (
  dataForm: MarkAsPaidModalForm,
  options?: SubmitMarkAsPaidOptions
) => Promise<boolean>

interface DirectTypeParams {
  label: string
  value: string
}

interface MarkAsPaidBodyModalParams {
  amountBilled: string
  customerName: string
  dueDate: string
  submitInvoice: SubmitMarkAsPaidHandler
  onCloseModal: () => void
  uuid: string
  uploadRef: any
  /** Dados da nota programada; ausente desliga o aviso de valor zero. */
  nfseContext?: ZeroAmountNfseContext
  /** Cancela o agendamento da nota. Resolve `false` se falhar. */
  onCancelScheduledNfse?: () => Promise<boolean>
  /**
   * A cobrança aceita nota avulsa e o usuário pode emitir: mostra a opção
   * "Emitir nota fiscal".
   */
  canEmitNfse?: boolean
}

type DialogProps = {
  isOpen: boolean
  description: string
  title: string
  handleClose: () => void
  action: () => void
  actionLabel: string
}

export const MarkAsPaidBodyModal = ({
  amountBilled,
  customerName,
  dueDate,
  submitInvoice,
  onCloseModal,
  uuid,
  uploadRef,
  nfseContext,
  onCancelScheduledNfse,
  canEmitNfse = false
}: MarkAsPaidBodyModalParams) => {
  const [loading, setLoading] = useState(false)
  const [dialog, setDialog] = useState<DialogProps>({
    isOpen: false,
    description: '',
    title: '',
    handleClose: () => null,
    action: () => null,
    actionLabel: ''
  })

  const canCancelNfse = !!CanLogical({ permission: 'nfses:cancel' })

  // Aviso de valor zero: guarda o form até o merchant decidir entre corrigir o
  // valor e seguir cancelando a nota.
  const [zeroNfseDialog, setZeroNfseDialog] = useState<{
    isOpen: boolean
    dataForm: MarkAsPaidModalForm | null
  }>({ isOpen: false, dataForm: null })

  // Uma vez cancelado o agendamento, o aviso não faz mais sentido e o DELETE não
  // pode rodar de novo — a retentativa do pagamento vai direto ao POST /pay.
  const [scheduledCancelled, setScheduledCancelled] = useState(false)

  const handleOpenDialog = ({
    title,
    description,
    handleClose,
    action,
    actionLabel
  }: {
    action: () => void
    handleClose: () => void
    title: string
    description: string
    actionLabel: string
  }) => {
    setDialog({
      title,
      description,
      isOpen: true,
      handleClose,
      action,
      actionLabel
    })
  }

  const handleCloseDialog = () => {
    setDialog((s) => ({ ...s, isOpen: false }))
  }

  const submit = async (
    dataForm: MarkAsPaidModalForm,
    options?: SubmitMarkAsPaidOptions
  ) => {
    setLoading(true)
    const data = {
      ...dataForm,
      amountPaid: formatCentsToSendToBackEnd(onlyNumber(dataForm.amountPaid))
    }
    const response = await submitInvoice(data, options)

    setLoading(response as boolean)
  }

  const handleCloseZeroNfseDialog = () => {
    setZeroNfseDialog({ isOpen: false, dataForm: null })
  }

  // Cancela o agendamento e só então paga. A ordem é obrigatória: assim que a
  // billing é paga o worker cria a nota (~1min) e o cancelamento passa a ser
  // recusado por NfseScheduled::Cancel.
  const handlePayCancellingNfse = async () => {
    const { dataForm } = zeroNfseDialog

    if (!dataForm || !onCancelScheduledNfse) return

    setLoading(true)
    const cancelled = await onCancelScheduledNfse()

    if (!cancelled) {
      setLoading(false)
      return
    }

    setScheduledCancelled(true)
    handleCloseZeroNfseDialog()
    await submit(dataForm, { nfseScheduledCancelled: true })
  }

  const handleClickToSubmit: SubmitHandler<MarkAsPaidModalForm> = async (
    dataForm
  ) => {
    if (
      !scheduledCancelled &&
      shouldWarnZeroAmountNfse(dataForm.amountPaid, nfseContext)
    ) {
      setZeroNfseDialog({ isOpen: true, dataForm })
      return
    }

    handleOpenDialog({
      title: 'Registrar Recebimento',
      description: 'Confirma registrar recebimento para este cliente?',
      handleClose: handleCloseDialog,
      action: () => submit(dataForm),
      actionLabel: 'Registrar'
    })
  }

  // A conta só faz sentido para quem enxerga o gráfico de contas a receber; para os
  // demais o campo nem aparece, e a baixa segue igual ao que era antes.
  const { hasAccess: canChooseAccount } = useModuleAccess(
    'accounts_receivable',
    'Contas a receber'
  )

  const { handleSubmit, control, setValue } = useForm<MarkAsPaidModalForm>({
    defaultValues: {
      amountPaid: currency(amountBilled || ''),
      datePaid: new Date(moment().format()),
      emitNfse: false
    } as MarkAsPaidModalForm,
    resolver: yupResolver(schemaFormMarkAsPaid)
  })

  const optionsPayment = [
    { label: 'Pix', value: 'pix' },
    { label: 'Cartão de Crédito', value: 'credit_card' },
    { label: 'Boleto', value: 'boleto' },
    { label: 'Dinheiro', value: 'cash' },
    { label: 'Transferência (TED/DOC)', value: 'transfer' },
    { label: 'Outro', value: 'other' }
  ]
  return (
    <ModalBody>
      <ModalHeader onClose={onCloseModal}>
        <Stack direction="row" alignItems="center" gap={2}>
          <IconContainer icon="paid" />
          <Typography variant="mdBold">Informações do pagamento</Typography>
        </Stack>
      </ModalHeader>

      <ModalContent>
        <Grid container={true} spacing={2}>
          <Grid item={true} xs={12}>
            {renderFields('Cliente: ', customerName)}
            {renderFields('Valor: ', formatMoney(Number(amountBilled)))}
            {renderFields('Vencimento: ', formatDate(dueDate) || '')}
          </Grid>
          <Grid item={true} xs={12}>
            <Controller
              control={control}
              name="datePaid"
              render={({
                field: { onChange, value },
                fieldState: { error }
              }) => (
                <DatePicker
                  helperText={error?.message}
                  error={!!error?.message}
                  label="Escolha a data de pagamento:"
                  onChange={onChange}
                  renderInput={(props: any) => <Textfield {...props} />}
                  toolbarPlaceholder="Escolha a data de vencimento"
                  value={value}
                />
              )}
            />
          </Grid>
          <Grid item={true} xs={6}>
            <Controller
              control={control}
              name="amountPaid"
              render={({
                field: { onChange, value },
                fieldState: { error }
              }) => (
                <Textfield
                  customStartAdornment={<Typography mr="6px">R$</Typography>}
                  helperText={error?.message}
                  error={!!error?.message}
                  label="Valor pago?"
                  mask="money"
                  name="amountPaid"
                  onChange={onChange}
                  startAdornment={true}
                  value={value}
                />
              )}
            />
          </Grid>
          <Grid item={true} xs={6}>
            <Controller
              control={control}
              name="directType"
              render={({ field: { onChange, value } }) => (
                <Autocomplete
                  onChange={(_, value) => {
                    const newValue = value as StateParams
                    onChange(newValue)
                  }}
                  options={optionsPayment}
                  noOptionsText="Nenhum meio de recebimento encontrado"
                  renderOption={(props, option) => {
                    const optionTyped = option as AutoCompleteBillingsParams
                    return (
                      <Box component="li" {...props} key={optionTyped.value}>
                        {optionTyped.label}
                      </Box>
                    )
                  }}
                  renderInput={(params) => (
                    <Textfield
                      helperText={
                        control.getFieldState('directType.value')?.error
                          ?.message
                      }
                      error={
                        !!control.getFieldState('directType.value')?.error
                          ?.message
                      }
                      label="Meio de recebimento:"
                      {...params}
                    />
                  )}
                  value={value}
                />
              )}
            />
          </Grid>
          {canChooseAccount && (
            <Grid item={true} xs={12}>
              <Controller
                control={control}
                name="source"
                render={({ field: { onChange, value } }) => (
                  <SourceAutocomplete
                    key={value?.value || 'source'}
                    value={value as { label: string; value: string }}
                    onChange={(
                      _: any,
                      newVal: { label: string; value: string }
                    ) => onChange(newVal ?? { value: '', label: '' })}
                    errorMessage=""
                    setValue={setValue}
                    label="Conta (opcional)"
                    labelTooltip={SETTLEMENT_ACCOUNT_HINT}
                    placeholder="Selecione a conta"
                    addButtonText="Adicionar conta"
                    modalTitle="Nova conta"
                    entityLabel="Conta"
                  />
                )}
              />
            </Grid>
          )}
          {canEmitNfse && (
            <Grid item={true} xs={12}>
              <Controller
                control={control}
                name="emitNfse"
                render={({ field: { onChange, value } }) => (
                  <EmitNfseOnReceiptSwitch
                    checked={!!value}
                    onChange={onChange}
                  />
                )}
              />
            </Grid>
          )}
          <Grid item={true} xs={12}>
            <UploadComponent
              uuid={uuid}
              title="Enviar comprovante"
              subTitle="Anexe aqui seu comprovante de pagamento."
              maxFiles={1}
              type="billings"
              noSuppressAuto={true}
              docType="receipt"
              ref={uploadRef}
              sentIcon={false}
              accept={{
                'application/pdf': ['.pdf'],
                'application/vnd.oasis.opendocument.text': ['.odt'],
                'application/msword': ['.doc'],
                'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
                  ['.docx'],
                'image/jpeg': ['.jpeg', '.jpg'],
                'image/png': ['.png'],
                'image/webp': ['.webp']
              }}
            />
          </Grid>
          <Grid item={true} xs={12}>
            <Controller
              control={control}
              name="comment"
              render={({
                field: { onChange, value },
                fieldState: { error }
              }) => (
                <Textarea
                  fullWidth={true}
                  onChange={onChange}
                  placeholder="Descrição"
                  value={value}
                  error={!!error?.message}
                  helperText={error?.message}
                  maxLength={2000}
                  style={{
                    overflow: 'auto',
                    height: '75px',
                    resize: 'vertical',
                    maxHeight: '50vh'
                  }}
                  label={
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <Box display="flex" gap="5px">
                        <Typography mt={0.5}>Descrição do pagamento</Typography>
                        <Tooltip
                          arrow
                          enterTouchDelay={0}
                          title="Este campo é destinado para adicionar informações complementares, exemplo: nome de outro responsável, nome do dependente, código de identificação, etc."
                        >
                          <Box position="relative">
                            <Icon
                              icon="info"
                              colorSX={theme.palette.neutral[600]}
                            />
                          </Box>
                        </Tooltip>
                      </Box>

                      <Typography
                        color={theme.palette.neutral[400]}
                        sx={{ fontSize: '12px', fontWeight: 300 }}
                      >
                        Caracteres: {value?.length ?? 0}/2000
                      </Typography>
                    </Stack>
                  }
                />
              )}
            />
          </Grid>
        </Grid>
      </ModalContent>

      <ModalFooter>
        <Button variantButton="neutral" onClick={onCloseModal}>
          Cancelar
        </Button>

        <Button
          variantButton="primary"
          onClick={handleSubmit(handleClickToSubmit, toastFirstError)}
        >
          Confirmar
        </Button>
      </ModalFooter>

      <Dialog
        icon="description"
        title={dialog.title}
        description={dialog.description}
        setIsOpenDialog={dialog.handleClose}
        isOpenDialog={dialog.isOpen}
        cancelButton
      >
        <Button
          autoFocus
          disabled={loading}
          onClick={dialog.action}
          loading={loading}
          fullWidth
        >
          {dialog.actionLabel}
        </Button>
      </Dialog>

      <Dialog
        icon="info"
        title="Esta cobrança ficará sem nota fiscal"
        description={
          'A nota desta cobrança é emitida pelo VALOR PAGO. Registrando o ' +
          'recebimento com R$ 0,00, a nota não pode ser emitida.'
        }
        setIsOpenDialog={handleCloseZeroNfseDialog}
        isOpenDialog={zeroNfseDialog.isOpen}
      >
        <Stack width="100%" gap={1}>
          {canCancelNfse && (
            <Button
              disabled={loading}
              loading={loading}
              onClick={handlePayCancellingNfse}
              fullWidth
            >
              Confirmar
            </Button>
          )}

          <Button
            autoFocus
            variantButton="neutral"
            disabled={loading}
            onClick={handleCloseZeroNfseDialog}
            fullWidth
          >
            Voltar e corrigir
          </Button>

          {!canCancelNfse && (
            <Stack direction="row" gap={0.5} alignItems="flex-start" mt={0.5}>
              <Icon icon="info" colorSX={theme.palette.neutral[600]} />
              <Typography variant="smLight" color={theme.palette.neutral[600]}>
                Cancelar o agendamento da nota exige a permissão de Notas
                Fiscais.
              </Typography>
            </Stack>
          )}
        </Stack>
      </Dialog>
    </ModalBody>
  )

  function renderFields(label: string, value: string) {
    return (
      <Box display="flex">
        <Box mr="4px">
          <Typography variant="smLight">{label}</Typography>
        </Box>
        <Box>
          <Typography variant="smBold">{value}</Typography>
        </Box>
      </Box>
    )
  }
}
